import { useElements } from '@stripe/react-stripe-js';
import type {
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementOptions,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
} from '@stripe/stripe-js';
import type { StripeElementChangeEvent } from '@stripe/stripe-js/types/stripe-js/elements/base';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import { InputError } from 'shared/ui/Input';

import style from './CardInputWrapper.module.scss';
import { requiredMessage } from './settings';

interface ChildrenParams {
  onChange: (x: StripeElementChangeEvent) => void;
  onBlur: () => void;
  onFocus: () => void;
  options: StripeCardNumberElementOptions;
}

const color = '#16191D';

const options: StripeCardNumberElementOptions | StripeCardCvcElementOptions | StripeCardExpiryElementOptions = {
  placeholder: '',
  style: {
    invalid: { color },
    base: {
      fontSize: '14px',
      fontFamily: 'Inter',
      fontWeight: 500,
      '::placeholder': {
        color: 'transparent',
      },
    },
  },
};

interface CardInputWrapperProps {
  children: (params: ChildrenParams) => ReactNode;
  label: string;
  type: 'cardNumber' | 'cardExpiry' | 'cardCvc';
  labelClass?: string;
  attributes?: DataAttributes['attributes'];
}

/*
We need to add those type fixes here in place as it's Stripe typings bug:
 https://github.com/stripe/stripe-js/issues/237
 */
interface FixedStripeElements {
  /**
   * Looks up a previously created `Element` by its type.
   */
  getElement(
    elementType: 'cardNumber' | 'cardExpiry' | 'cardCvc'
  ): StripeCardNumberElement | StripeCardExpiryElement | StripeCardCvcElement | null;

  getElement(elementType: 'cardNumber'): StripeCardNumberElement | null;
  getElement(elementType: 'cardExpiry'): StripeCardExpiryElement | null;
  getElement(elementType: 'cardCvc'): StripeCardCvcElement | null;
}

export function CardInputWrapper({ type, children, label, labelClass, attributes }: CardInputWrapperProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const methods = useFormContext();
  const stripeElements = useElements() as FixedStripeElements | null;

  const onWrapperClickHandler = () => {
    if (isFocused || !stripeElements) {
      return null;
    }

    stripeElements.getElement(type)?.focus();
  };

  const params: ChildrenParams = {
    onChange: (element: StripeElementChangeEvent) => {
      setIsEmpty(element.empty);
      methods.clearErrors(type);
      if (element.empty) {
        methods.setValue(type, '');
      } else {
        methods.setValue(type, 'any_value', {
          shouldDirty: true,
        });
      }
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    options: {
      ...options,
      placeholder: type,
    },
  };

  const isError = !!methods.formState.errors[type]?.message;
  const childrenClass = classNames('relative', style.cardInput, {
    [style.cardInputFocus]: isFocused && !isError,
    [style.activeLabel]: !isEmpty || isFocused,
    [style.errorLabel]: isError,
    [style.cardInputError]: isError,
    [style.activeLabelFocus]: !isError && isFocused,
  });

  return (
    <div
      className="mb-2"
      data-testid="TestId_CardInputWrapper"
      {...attributes}
    >
      <input
        style={{ display: 'none' }}
        {...methods.register(type, {
          required: requiredMessage,
        })}
      />
      <div
        className={childrenClass}
        onClick={onWrapperClickHandler}
        data-testid="TestId_CardInputWrapper__ChildrenWrapper"
      >
        {children(params)}
        <label className={classNames(style.label, labelClass)}>{label}</label>
      </div>
      <InputError name={type} />
    </div>
  );
}
